import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Trash2, ExternalLink, Eye, EyeOff, Play, Square, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import SelectKeyDialog from '@/components/ui/SelectKeyDialog';

const Profile = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [setups, setSetups] = useState([]);
  const [loadingSetups, setLoadingSetups] = useState(true);
  const [exchanges, setExchanges] = useState([]);
  const [tradingStatuses, setTradingStatuses] = useState({});
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [selectedSetupId, setSelectedSetupId] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      fetchSetups();
      fetchExchanges();
    }
  }, [user]);

  const fetchExchanges = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/exchanges/connections`, {
        withCredentials: true
      });
      setExchanges(response.data);
    } catch (error) {
      console.error('Error fetching exchanges:', error);
    }
  };

  const fetchSetups = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/setups`, {
        withCredentials: true
      });
      setSetups(response.data);
      
      // Fetch trading status for each setup
      for (const setup of response.data) {
        fetchTradingStatus(setup.id);
      }
    } catch (error) {
      console.error('Error fetching setups:', error);
      toast.error('Ошибка загрузки сетапов');
    } finally {
      setLoadingSetups(false);
    }
  };

  const fetchTradingStatus = async (setupId) => {
    try {
      const response = await axios.get(`${backendUrl}/api/setups/${setupId}/trading-status`, {
        withCredentials: true
      });
      setTradingStatuses(prev => ({ ...prev, [setupId]: response.data }));
    } catch (error) {
      console.error(`Error fetching trading status for ${setupId}:`, error);
    }
  };

  const deleteSetup = async (setupId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот сетап?')) {
      return;
    }

    try {
      await axios.delete(`${backendUrl}/api/setups/${setupId}`, {
        withCredentials: true
      });
      setSetups(setups.filter(s => s.id !== setupId));
      toast.success('Сетап удалён');
    } catch (error) {
      console.error('Error deleting setup:', error);
      toast.error('Ошибка удаления сетапа');
    }
  };

  const copyShareLink = (setupId) => {
    const link = `${window.location.origin}/setup/${setupId}`;
    navigator.clipboard.writeText(link);
    toast.success('Ссылка скопирована в буфер обмена');
  };

  const handleActivateTrading = async (setupId, connectionId) => {
    try {
      await axios.post(
        `${backendUrl}/api/setups/${setupId}/activate-trading`,
        { connection_id: connectionId },
        { withCredentials: true }
      );
      toast.success('Торговля активирована!');
      fetchTradingStatus(setupId);
    } catch (error) {
      console.error('Error activating trading:', error);
      toast.error(error.response?.data?.detail || 'Ошибка активации торговли');
    }
  };

  const handleLaunchSetup = (setupId) => {
    setSelectedSetupId(setupId);
    setShowKeyDialog(true);
  };

  const handleKeySelected = (key) => {
    if (selectedSetupId && key.id) {
      handleActivateTrading(selectedSetupId, key.id);
    }
  };

  const handleStopTrading = async (setupId) => {
    if (!window.confirm('Остановить автоматическую торговлю для этого сетапа?')) {
      return;
    }

    try {
      await axios.post(
        `${backendUrl}/api/setups/${setupId}/stop-trading`,
        {},
        { withCredentials: true }
      );
      toast.success('Торговля остановлена');
      fetchTradingStatus(setupId);
    } catch (error) {
      console.error('Error stopping trading:', error);
      toast.error('Ошибка остановки торговли');
    }
  };

  if (loading || loadingSetups) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Header />
        <div className="pt-20 pb-8 px-4 md:px-6 max-w-7xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-800 rounded w-1/4"></div>
            <div className="h-64 bg-gray-800 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const stats = {
    total: setups.length,
    active: setups.filter(s => s.status === 'active').length,
    completed: setups.filter(s => s.status === 'completed').length,
    totalProfit: setups
      .filter(s => s.status === 'completed') // Считаем только завершенные
      .reduce((sum, s) => {
        const finalBalance = s.steps[s.steps.length - 1]?.balance_after || s.initial_balance;
        return sum + (finalBalance - s.initial_balance);
      }, 0)
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Toaster position="top-right" />
      <Header />
      
      <div className="pt-20 pb-8 px-4 md:px-6 max-w-7xl mx-auto">
        {/* User Info */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Мой профиль</h1>
          <p className="text-gray-400">{user.email}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-400 mb-1">Всего сетапов</p>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-400 mb-1">Активные</p>
                <p className="text-2xl font-bold text-[#00ff88]">{stats.active}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-400 mb-1">Завершённые</p>
                <p className="text-2xl font-bold text-[#00ff88]">{stats.completed}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-400 mb-1">Общий профит</p>
                <p className={`text-2xl font-bold ${stats.totalProfit >= 0 ? 'text-[#00ff88]' : 'text-red-500'}`}>
                  ${stats.totalProfit.toFixed(2)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Setups List */}
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white mb-4">Мои сетапы</h2>
          
          {setups.length === 0 ? (
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardContent className="pt-6 text-center text-gray-400">
                <p>У вас пока нет сохранённых сетапов</p>
                <Button
                  onClick={() => navigate('/')}
                  className="mt-4 bg-gradient-to-r from-[#00ff88] to-[#00cc6f] text-black hover:opacity-90"
                >
                  Создать сетап
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {setups.map((setup) => {
                const finalBalance = setup.steps[setup.steps.length - 1]?.balance_after || setup.initial_balance;
                const profit = finalBalance - setup.initial_balance;
                const profitPercent = ((profit / setup.initial_balance) * 100).toFixed(2);
                
                // Calculate progress by points (entry + exit for each step)
                const totalPoints = setup.steps.length * 2;  // Each step has 2 points
                const reachedPoints = setup.steps.reduce((sum, step) => {
                  let points = 0;
                  if (step.entry_reached) points += 1;  // Entry reached
                  if (step.reached) points += 1;  // Exit reached
                  return sum + points;
                }, 0);

                return (
                  <Card key={setup.id} className="bg-[#1a1a1a] border-gray-800">
                    <CardHeader>
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
                        <div className="flex-1">
                          <CardTitle className="text-white text-lg md:text-xl">{setup.title}</CardTitle>
                          <CardDescription className="text-gray-400">
                            Создан: {new Date(setup.created_at).toLocaleDateString('ru-RU')}
                          </CardDescription>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {tradingStatuses[setup.id]?.active && (
                            <Badge 
                              variant="outline" 
                              className="bg-[#00ff88]/20 text-[#00ff88] border-[#00ff88]/50 animate-pulse"
                            >
                              🤖 Торгует
                            </Badge>
                          )}
                          <Badge 
                            variant="outline" 
                            className={`${
                              setup.status === 'completed' 
                                ? 'bg-[#00ff88]/20 text-[#00ff88] border-[#00ff88]/50' 
                                : setup.status === 'active'
                                ? 'bg-blue-500/20 text-blue-400 border-blue-500/50'
                                : 'bg-red-500/20 text-red-400 border-red-500/50'
                            }`}
                          >
                            {setup.status === 'completed' ? 'Завершён' : setup.status === 'active' ? 'Активный' : 'Провален'}
                          </Badge>
                          <Badge 
                            variant="outline" 
                            className="bg-gray-800/50 text-gray-400 border-gray-700"
                          >
                            {setup.is_public ? <Eye className="h-3 w-3 mr-1" /> : <EyeOff className="h-3 w-3 mr-1" />}
                            {setup.is_public ? 'Публичный' : 'Приватный'}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-400">Вложения</p>
                          <p className="text-lg font-semibold text-white">${setup.initial_balance.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Потенциал</p>
                          <p className="text-lg font-semibold text-white">${finalBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Профит</p>
                          <p className={`text-lg font-semibold ${profit >= 0 ? 'text-[#00ff88]' : 'text-red-500'}`}>
                            {profit >= 0 ? <TrendingUp className="inline h-4 w-4 mr-1" /> : <TrendingDown className="inline h-4 w-4 mr-1" />}
                            ${profit.toFixed(2)} ({profitPercent}%)
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Шагов</p>
                          <p className="text-lg font-semibold text-white">{setup.steps.length}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Прогресс</p>
                          <p className="text-lg font-semibold text-[#00ff88]">{reachedPoints}/{totalPoints}</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/setup/${setup.id}`)}
                          className="border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Открыть
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyShareLink(setup.id)}
                          className="border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800"
                        >
                          Скопировать ссылку
                        </Button>
                        
                        {/* Trading controls */}
                        {setup.status === 'completed' ? (
                          <Badge className="bg-gray-700/50 text-gray-400">
                            Сетап завершен
                          </Badge>
                        ) : exchanges.length > 0 ? (
                          tradingStatuses[setup.id]?.active ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStopTrading(setup.id)}
                              className="border-red-700 text-red-400 hover:text-red-300 hover:bg-red-900/20"
                            >
                              <Square className="h-4 w-4 mr-2" />
                              Остановить торговлю
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleLaunchSetup(setup.id)}
                              className="border-[#00ff88] text-[#00ff88] hover:bg-[#00ff88]/10"
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Запустить сетап
                            </Button>
                          )
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate('/exchanges')}
                            className="border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800"
                          >
                            <Link2 className="h-4 w-4 mr-2" />
                            Подключить биржу
                          </Button>
                        )}
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteSetup(setup.id)}
                          className="border-red-900/50 text-red-400 hover:text-red-300 hover:bg-red-900/20"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Удалить
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Key Selection Dialog */}
      <SelectKeyDialog
        isOpen={showKeyDialog}
        onClose={() => {
          setShowKeyDialog(false);
          setSelectedSetupId(null);
        }}
        onSelectKey={handleKeySelected}
        setupId={selectedSetupId}
      />
    </div>
  );
};

export default Profile;
